import React from 'react';

import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { programSel } from '@context/duck/selectorsTyped';
import { AdvancementsTabsNames } from '@modules/advancement/pendingItems/constants';
import { getNotificationCountActivity } from '@modules/advancement/purchaseOrder/utils';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { useUnitPendingLeadershipPositions } from '@modules/leadershipPositions/hooks';
import {
  useGetOrgActivityDashboardQuery,
  useGetOrgAdvancementsDashboardQuery,
} from '@modules/rtk/esb-api';
import { Tooltip } from '@modules/shared/components';
import { Space } from '@modules/shared/components/Space/Space';
import { intl } from '@modules/shared/localization';
import {
  hasNotificationsToShowSel,
  notificationCountsSel,
  openAdvancementReportModal,
} from '@modules/userNotifications/duck';
import { setNotificationsVisible } from '@modules/userNotifications/duck/slice';
import {
  Program,
  activityLookbackDate,
  advancementTabNames,
} from '@shared/constants';
import { goToRosterRoute } from '@shared/duck/actionsTyped';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { isUnitTypeSel } from '@unit/duck/selectorsTyped';

import BadgeNotificationItem from './BadgeNotificationItem/BadgeNotificationItem';
import styles from './Notifications.less';

export const NotificationsModalContentV2: React.FC = () => {
  const dispatch = useDispatch();
  const { pendingCount: positionsCount } = useUnitPendingLeadershipPositions();
  const hasNotificationsToShow = useSelector(hasNotificationsToShowSel);
  const orgGuid = useSelector(organizationGuidSel);
  const isUnitOrg = useSelector(isUnitTypeSel);
  const counts = useSelector(notificationCountsSel);
  const program = useSelector(programSel);
  const { data: stats } = useGetOrgAdvancementsDashboardQuery(
    { organizationGuid: orgGuid },
    {
      skip: !orgGuid || !isUnitOrg,
    },
  );

  const { data: activitiesCount } = useGetOrgActivityDashboardQuery(
    {
      organizationGuid: orgGuid,
      startDate: activityLookbackDate(),
    },
    { skip: !orgGuid || !isUnitOrg },
  );

  const countActivity = activitiesCount
    ? getNotificationCountActivity(activitiesCount)
    : 0;

  const toApproveCount = counts.toApproveCount + countActivity;

  if (
    !hasNotificationsToShow &&
    !positionsCount &&
    !counts.totalCounts &&
    !countActivity
  ) {
    return <FormattedMessage id="userNotifications.Notifications.empty" />;
  }

  const onOpenAdvancementItem = () => {
    dispatch(
      goToRosterRoute({
        activeTab: advancementTabNames.ADVANCEMENTS,
        advancementsTab: AdvancementsTabsNames.TO_APPROVE,
      }),
    );
    dispatch(setNotificationsVisible(false));
  };

  const onOpenAdvancementReportModal = () =>
    dispatch(openAdvancementReportModal());

  const toApproveItems = [
    [
      stats?.completed?.ranks,
      'userNotifications.Notifications.toApprove.ranksCount',
      { count: Number(stats?.completed?.ranks) },
    ],
    program === Program.CUB_SCOUT && [
      stats?.completed?.adventures,
      'userNotifications.Notifications.toApprove.adventuresCount',
      { count: Number(stats?.completed?.adventures) },
    ],
    program !== Program.CUB_SCOUT && [
      stats?.completed?.meritBadges,
      'userNotifications.Notifications.toApprove.mbsCount',
      { count: Number(stats?.completed?.meritBadges) },
    ],
    [
      stats?.completed?.awards,
      'userNotifications.Notifications.toApprove.awardsCount',
      { count: Number(stats?.completed?.awards) },
    ],
    featureFlags.getFlag('SBL_4724_PENDING_REQUIREMENTS') && [
      _.sum(Object.values(stats?.completed?.requirements || {})),
      'userNotifications.Notifications.toApprove.reqsCount',
      { count: _.sum(Object.values(stats?.completed?.requirements || {})) },
    ],
    [
      positionsCount,
      'userNotifications.Notifications.toApprove.positionsCount',
      { count: positionsCount || 0 },
    ],
    [
      countActivity,
      'userNotifications.Notifications.toApprove.activitiesCount',
      { count: countActivity },
    ],
  ].filter(Boolean) as Array<[number, string, { count: number }]>;

  const tooltip = toApproveItems
    .map(item => {
      const [num, id, values] = item;
      if (!num) return null;

      const msg = intl.formatMessage(
        {
          id: id,
        },
        values,
      );
      return msg;
    })
    .filter(Boolean)
    .join(', ');

  return (
    <div className={styles.notificationsWrapper}>
      {toApproveCount > 0 && (
        <Tooltip title={tooltip} placement="right">
          <div>
            <BadgeNotificationItem
              count={toApproveCount}
              onClick={onOpenAdvancementItem}
              title={
                <FormattedMessage id="userNotifications.Notifications.itemsToApprove" />
              }
              description={
                <Space size="xxs">
                  <FormattedMessage
                    id="userNotifications.Notifications.itemsToApproveDescription"
                    values={{ pendingItemsToApprove: toApproveCount }}
                  />
                </Space>
              }
            />
          </div>
        </Tooltip>
      )}

      {counts.toPurchaseCount > 0 && (
        <BadgeNotificationItem
          id="qa_notificationAdvReport"
          title={
            <FormattedMessage id="userNotifications.NotificationsModalContent.needsPurchasing" />
          }
          description={
            <FormattedMessage id="userNotifications.NotificationsModalContent.viewScoutShopReport" />
          }
          count={counts.toPurchaseCount}
          onClick={onOpenAdvancementReportModal}
        />
      )}
    </div>
  );
};
